import { useCallback, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/useSession';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Chip } from '../../components/Chip';
import { EmptyState } from '../../components/EmptyState';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Table, type Column } from '../../components/Table';
import { copyToClipboard, errorMessage, formatDate, useLoad } from './hooks';
import { assignableRoles, requiresMfa, ROLE_HELP, ROLE_LABEL, type OrgRole } from './roles';
import type { Invitation, Member } from './types';

interface MembersData {
  members: Member[];
  invitations: Invitation[];
}

type RemoveState =
  | { member: Member; step: 'confirm'; error?: string }
  | { member: Member; step: 'custody'; count: number };

export function MembersPage() {
  const { activeOrg, user } = useSession();
  const orgId = activeOrg?.orgId;
  const loader = useCallback(async (): Promise<MembersData> => {
    const [members, invitations] = await Promise.all([
      api.get<{ items: Member[] }>('/v1/members', { orgId }),
      api.get<{ items: Invitation[] }>('/v1/members/invitations', { orgId }),
    ]);
    return { members: members.items, invitations: invitations.items };
  }, [orgId]);
  const { data, error, loading, reload } = useLoad(loader);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [removing, setRemoving] = useState<RemoveState | null>(null);
  const [busy, setBusy] = useState(false);
  const closeInvite = useCallback(() => setInviteOpen(false), []);
  const closeRemove = useCallback(() => setRemoving(null), []);

  async function changeRole(member: Member, role: OrgRole) {
    if (role === member.role) return;
    setNotice(null);
    try {
      await api.patch(`/v1/members/${encodeURIComponent(member.uid)}`, { role }, { orgId });
      setNotice({ tone: 'success', text: `${member.email ?? 'El miembro'} ahora es ${ROLE_LABEL[role]}.` });
      await reload();
    } catch (err) {
      setNotice({ tone: 'error', text: errorMessage(err) });
    }
  }

  async function confirmRemove() {
    if (!removing || removing.step !== 'confirm') return;
    setBusy(true);
    try {
      const res = await api.del<{ sessionsRevoked: boolean }>(`/v1/members/${encodeURIComponent(removing.member.uid)}`, { orgId });
      setNotice({
        tone: 'success',
        text: res?.sessionsRevoked === false
          ? `Removimos a ${removing.member.email}. No pudimos cerrar sus sesiones: perderá el acceso en su próxima solicitud.`
          : `Removimos a ${removing.member.email} y cerramos sus sesiones.`,
      });
      setRemoving(null);
      await reload();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'custody_pending') {
        const count = (err.details as { count?: number } | undefined)?.count ?? 0;
        setRemoving({ member: removing.member, step: 'custody', count });
      } else {
        setRemoving({ ...removing, error: errorMessage(err) });
      }
    } finally {
      setBusy(false);
    }
  }

  const myRole = activeOrg?.role;
  const roleOptions = assignableRoles(myRole);

  const columns: Column<Member>[] = [
    { key: 'email', header: 'Email', render: (m) => <span>{m.email ?? m.uid}{m.uid === user?.uid && <span className="ml-1 text-ink-muted">(tú)</span>}</span> },
    {
      key: 'role',
      header: 'Rol',
      render: (m) => {
        const editable = m.uid !== user?.uid && (m.role !== 'owner' || myRole === 'owner');
        if (!editable) return ROLE_LABEL[m.role];
        return (
          <label className="flex items-center gap-2">
            <span className="sr-only">Rol de {m.email}</span>
            <select
              className="rounded-sm border border-line bg-white px-2 py-1 text-sm"
              value={m.role}
              onChange={(e) => void changeRole(m, e.target.value as OrgRole)}
            >
              {(roleOptions.includes(m.role) ? roleOptions : [m.role, ...roleOptions]).map((r) => (
                <option key={r} value={r}>{ROLE_LABEL[r]}</option>
              ))}
            </select>
          </label>
        );
      },
    },
    {
      key: 'mfa',
      header: 'Segundo factor',
      render: (m) => {
        if (!requiresMfa(m.role)) return <span className="text-ink-muted">No requerido</span>;
        if (m.mfaEnrolled === false) return <Chip tone="warning">Pendiente</Chip>;
        return <Chip tone="success">Obligatorio</Chip>;
      },
    },
    { key: 'joined', header: 'Desde', render: (m) => formatDate(m.joinedAt) },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (m) =>
        m.uid === user?.uid || (m.role === 'owner' && myRole !== 'owner') ? (
          <span className="text-ink-muted">—</span>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => setRemoving({ member: m, step: 'confirm' })}>
            Remover
          </Button>
        ),
    },
  ];

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">Miembros</h1>
          <p className="text-sm text-ink-muted">Quién puede entrar a {activeOrg?.orgName ?? 'la organización'} y qué puede hacer.</p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>Invitar miembro</Button>
      </div>

      <div aria-live="polite">{notice && <Banner tone={notice.tone}>{notice.text}</Banner>}</div>
      {error && <Banner tone="error" action={<Button variant="secondary" size="sm" onClick={() => void reload()}>Reintentar</Button>}>{error}</Banner>}

      <Table
        caption="Miembros de la organización"
        columns={columns}
        rows={data?.members ?? []}
        rowKey={(m) => m.uid}
        loading={loading && !data}
        empty={<EmptyState title="Aún no hay miembros" description="Invita a tu equipo para repartir el trabajo." />}
      />

      {data && data.invitations.length > 0 && (
        <div className="space-y-2">
          <h2 className="font-display text-base font-bold text-ink">Invitaciones pendientes</h2>
          <ul className="divide-y divide-line rounded-lg border border-line bg-white">
            {data.invitations.map((inv) => (
              <li key={inv.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
                <span>{inv.email}</span>
                <span className="text-ink-muted">{ROLE_LABEL[inv.role]} · vence el {formatDate(inv.expiresAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-xs text-ink-muted">
        Un Admin no puede crear, degradar ni remover a un Owner, y la organización nunca queda sin Owner.
      </p>

      <InviteModal
        open={inviteOpen}
        orgId={orgId}
        roles={roleOptions}
        onClose={closeInvite}
        onInvited={async (inv) => {
          setNotice({ tone: 'success', text: `Enviamos la invitación a ${inv.email}. Vence en 7 días.` });
          await reload();
        }}
      />

      <Modal
        open={removing?.step === 'confirm'}
        title={`Remover a ${removing?.member.email ?? ''}`}
        onClose={closeRemove}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemoving(null)}>Cancelar</Button>
            <Button variant="danger" loading={busy} onClick={() => void confirmRemove()}>Remover</Button>
          </>
        }
      >
        <p className="text-sm text-ink">Perderá el acceso en su próxima solicitud y cerraremos sus sesiones activas.</p>
        {removing?.step === 'confirm' && removing.error && <Banner tone="error">{removing.error}</Banner>}
      </Modal>

      <Modal
        open={removing?.step === 'custody'}
        title={`No se puede remover a ${removing?.member.email ?? ''} todavía`}
        onClose={closeRemove}
        size="sm"
        footer={<Button variant="secondary" onClick={() => setRemoving(null)}>Entendido</Button>}
      >
        <p className="text-sm text-ink">
          Tiene <b>{removing?.step === 'custody' ? removing.count : 0} QR en custodia</b>. Reasígnalos a otra persona antes de removerla.
        </p>
        <Link to="/consola/inventario" className="text-sm">Ir al inventario para reasignar</Link>
      </Modal>
    </section>
  );
}

function InviteModal({
  open,
  orgId,
  roles,
  onClose,
  onInvited,
}: {
  open: boolean;
  orgId: string | undefined;
  roles: OrgRole[];
  onClose: () => void;
  onInvited: (inv: Invitation) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('seller');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Invitation | null>(null);
  const [copied, setCopied] = useState(false);

  // `Modal` reenfoca el diálogo cuando cambia `onClose`: debe ser estable para no quitar el foco al escribir.
  const close = useCallback(() => {
    setEmail('');
    setRole('seller');
    setError(null);
    setResult(null);
    setCopied(false);
    onClose();
  }, [onClose]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError('Escribe un email válido, por ejemplo ana@tuempresa.cl.');
      return;
    }
    setLoading(true);
    try {
      const inv = await api.post<Invitation>('/v1/members/invitations', { email: email.trim(), role }, { orgId });
      await onInvited(inv);
      if (inv.acceptUrl) setResult(inv);
      else close();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open={open} title="Invitar miembro" onClose={close}>
      {result ? (
        <div className="space-y-3">
          <Banner tone="success">Invitación creada para {result.email}.</Banner>
          <p className="text-sm text-ink-muted">En este ambiente de pruebas no se envía el correo. Comparte este enlace:</p>
          <code className="block break-all rounded-sm bg-mist px-2 py-1 font-mono text-xs">{result.acceptUrl}</code>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={async () => setCopied(await copyToClipboard(result.acceptUrl ?? ''))}>
              {copied ? 'Copiado' : 'Copiar enlace'}
            </Button>
            <Button onClick={close}>Listo</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Input label="Email" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} error={error ?? undefined} />
          <fieldset className="space-y-2">
            <legend className="text-xs font-medium text-ink-muted">Rol</legend>
            {roles.map((r) => (
              <label key={r} className="flex items-start gap-2 rounded-md border border-line px-3 py-2 text-sm has-[:checked]:border-green has-[:checked]:bg-green-soft">
                <input type="radio" name="invite-role" value={r} checked={role === r} onChange={() => setRole(r)} className="mt-1" />
                <span>
                  <b>{ROLE_LABEL[r]}</b>
                  <span className="block text-xs text-ink-muted">{ROLE_HELP[r]}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <p className="text-xs text-ink-muted">
            La invitación es de un solo uso y vence en 7 días.{requiresMfa(role) ? ' Este rol deberá configurar segundo factor.' : ''}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={close}>Cancelar</Button>
            <Button type="submit" loading={loading}>Enviar invitación</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
